import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../data/translations';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const companyInfo = {
    phone: '+243 97 13 27 318',
    email: 'infos@gci-rdc.com'
  };

  const aboutSubmenu = [
    { name: language === 'fr' ? 'GCI Entreprise engagée' : 'GCI Committed Company', path: '/qui-sommes-nous/gci-entreprise-engagee' },
    { name: language === 'fr' ? 'Notre équipe' : 'Our team', path: '/qui-sommes-nous/notre-equipe' },
    { name: language === 'fr' ? 'Nos partenaires' : 'Our partners', path: '/qui-sommes-nous/nos-partenaires' },
    { name: language === 'fr' ? 'Espace documents' : 'Documents area', path: '/qui-sommes-nous/espace-documents' },
  ];

  const menuItems = [
    { name: getTranslation(language, 'home'), href: '/', path: '/' },
    { name: getTranslation(language, 'aboutUs'), href: '/qui-sommes-nous/gci-entreprise-engagee', path: '/qui-sommes-nous', submenu: aboutSubmenu },
    { name: getTranslation(language, 'news'), href: '/actualites', path: '/actualites' },
    { name: getTranslation(language, 'yourProject'), href: '/votre-projet', path: '/votre-projet' },
    { name: getTranslation(language, 'publications'), href: '/publications', path: '/publications' }
  ];

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/qui-sommes-nous') {
      return location.pathname.startsWith('/qui-sommes-nous');
    }
    return location.pathname === path;
  };

  return (
    <header className="fixed w-full top-0 z-[1100] bg-white shadow-md">
      {/* Oversized floating LOGO — positioned in the LEFT white margin of the viewport */}
      <div
        className="hidden lg:block absolute left-4 xl:left-10 top-2 z-30 pointer-events-none"
        data-testid="header-logo-float"
      >
        <div className="w-[150px] h-[150px] rounded-full overflow-hidden shadow-2xl border-[5px] border-orange-500 ring-2 ring-[#2f2f30] bg-white pointer-events-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_166b91da-a769-4162-9f6c-f6ec47415337/artifacts/h0jkwlmz_Capture%20d%E2%80%99e%CC%81cran%202026-02-16%20a%CC%80%2020.38.46.png"
            alt="Global Construction Logo"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto relative">
        {/* Top colored bar: charcoal (left ~70%) + orange (right ~30%) */}
        <div className="flex items-stretch w-full" data-testid="top-colored-bar">
          {/* LEFT — charcoal: Siège Kinshasa (logo floats in the left white margin on lg+) */}
          <div
            className="flex items-center gap-6 pr-6 py-6 flex-1 lg:flex-none lg:pl-[80px] pl-8"
            style={{ backgroundColor: '#2f2f30', flex: '1 1 70%' }}
            data-testid="top-bar-left"
          >
            {/* Small logo visible only below lg */}
            <div className="lg:hidden w-20 h-20 rounded-full overflow-hidden shadow-lg border-4 border-orange-500 flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_166b91da-a769-4162-9f6c-f6ec47415337/artifacts/h0jkwlmz_Capture%20d%E2%80%99e%CC%81cran%202026-02-16%20a%CC%80%2020.38.46.png"
                alt="Global Construction Logo"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="hidden lg:flex items-center gap-4">
              <div className="w-14 h-14 rounded-full border-2 border-white/80 flex items-center justify-center flex-shrink-0">
                <Phone size={24} className="text-white" />
              </div>
              <div className="text-white">
                <p className="text-sm font-semibold opacity-90">
                  {language === 'fr' ? 'Siège - Kinshasa' : 'Headquarters - Kinshasa'}
                </p>
                <p className="text-xl font-bold" data-testid="top-bar-phone">
                  {companyInfo.phone}
                </p>
              </div>
            </div>

            <div className="ml-auto hidden lg:block">
              <LanguageSelector />
            </div>

            {/* Mobile Menu Button (inside dark area on small screens) */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden ml-auto text-white hover:text-orange-400 transition-colors"
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X size={32} /> : <Menu size={32} />}
            </button>
          </div>

          {/* RIGHT — orange: contact email */}
          <div
            className="hidden lg:flex items-center gap-4 px-8 py-6"
            style={{ backgroundColor: '#FF8C00', flex: '0 0 30%' }}
            data-testid="top-bar-right"
          >
            <div className="w-14 h-14 rounded-full border-2 border-white/80 flex items-center justify-center flex-shrink-0">
              <Mail size={24} className="text-white" />
            </div>
            <div className="text-white min-w-0">
              <p className="text-sm font-semibold opacity-90">
                {language === 'fr' ? 'Contact Email' : 'Contact Email'}
              </p>
              <a
                href={`mailto:${companyInfo.email}`}
                className="text-xl font-bold hover:underline break-all"
                data-testid="top-bar-email"
              >
                {companyInfo.email}
              </a>
            </div>
          </div>
        </div>

        {/* Navigation Menu - All on One Line */}
        <nav className="hidden lg:block bg-gray-50" data-testid="main-nav">
          <div className="flex items-center justify-center gap-1 px-8 py-4">
            {menuItems.map((item) => (
              <div key={item.name} className="relative group">
                <a
                  href={item.href}
                  className={`relative inline-flex items-center px-6 py-3 font-bold text-base tracking-wide transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-orange-500'
                      : 'text-gray-700 hover:text-orange-500'
                  }`}
                  data-testid={`menu-item-${item.path}`}
                >
                  <span className="relative z-10">{item.name}</span>
                  {/* Underline animation */}
                  <span
                    className={`absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-orange-500 to-orange-600 transform origin-left transition-transform duration-300 ${
                      isActive(item.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                  {/* Background animation on hover */}
                  <span
                    className={`absolute inset-0 -z-10 bg-orange-50 rounded-lg transform scale-0 transition-transform duration-300 ${
                      isActive(item.path)
                        ? 'scale-100 opacity-50'
                        : 'group-hover:scale-100 group-hover:opacity-100'
                    }`}
                  />
                </a>

                {/* Submenu dropdown */}
                {item.submenu && (
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full mt-1 min-w-[260px] bg-white shadow-2xl border border-gray-100 rounded-md invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 z-[1200]"
                    data-testid={`submenu-${item.path}`}
                  >
                    <div className="py-2">
                      {item.submenu.map((sub) => (
                        <a
                          key={sub.path}
                          href={sub.path}
                          className={`block px-5 py-3 text-sm font-semibold transition-colors ${
                            location.pathname === sub.path
                              ? 'text-orange-500 bg-orange-50'
                              : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                          }`}
                          data-testid={`submenu-link-${sub.path}`}
                        >
                          {sub.name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-6 py-4 space-y-3">
              {menuItems.map((item) => (
                <div key={item.name}>
                  <a
                    href={item.href}
                    onClick={() => !item.submenu && setIsMobileMenuOpen(false)}
                    className={`block font-semibold text-base tracking-wide transition-all duration-300 px-6 py-3 rounded-lg ${
                      isActive(item.path)
                        ? 'text-orange-500 bg-orange-50'
                        : 'text-gray-700 hover:text-orange-500 hover:bg-orange-50'
                    }`}
                  >
                    {item.name}
                  </a>
                  {item.submenu && (
                    <div className="pl-6 mt-1 space-y-1">
                      {item.submenu.map((sub) => (
                        <a
                          key={sub.path}
                          href={sub.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-sm text-gray-600 hover:text-orange-500 px-4 py-2 rounded"
                        >
                          — {sub.name}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;